import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchAuthenticatedBlobUrl, ApiError } from "@/lib/apiClient";
import { GRADE_COLORS, getGradeColor } from "@/lib/gradeColors";
import { clampNormalized, renderPlanSvg, type PlanMarkerInput } from "@/lib/planViewer";
import "./wartungPlan.css";

export type PortalWartungErgebnis = {
  id: number;
  result_status?: string | null;
  result_status_label?: string | null;
  grade_final?: number | null;
  performed_at?: string | null;
  completed_at?: string | null;
  type_key?: string | null;
  type_name?: string | null;
  asset_id?: number | null;
  asset_external_id?: string | null;
  asset_name?: string | null;
  defect_count?: number;
  has_defects?: boolean;
};

export type PortalPlanPoint = {
  asset_id: number;
  x?: number | null;
  y?: number | null;
  label_x?: number | null;
  label_y?: number | null;
  label_text?: string | null;
  page_number?: number;
  grade_final?: number | null;
  asset_external_id?: string | null;
  asset_name?: string | null;
  event_id?: number | null;
  result_status?: string | null;
  result_status_label?: string | null;
  plan_label_symbol?: {
    svg_markup?: string | null;
    png_preview_base64?: string | null;
  } | null;
};

export type PortalWartungDetail = {
  project: {
    project_id: number;
    ort_name?: string | null;
    auftragsnummer?: string | null;
    beschreibung?: string | null;
    kunde_name?: string | null;
  };
  sub_locations: Array<{
    id: number;
    name?: string | null;
    path_label?: string;
    path_segments?: string[];
    plan_ids: number[];
    active_plan_id: number | null;
    has_plan: boolean;
  }>;
  plaene: Array<{
    id: number;
    title?: string | null;
    sub_location_id?: number | null;
    page?: number;
    page_number?: number;
    marker_overlay_scale?: number;
    has_file?: boolean;
    points: PortalPlanPoint[];
  }>;
  ergebnisse?: PortalWartungErgebnis[];
};

type Props = {
  projectId: number;
  detail: PortalWartungDetail;
  onClose: () => void;
};

type DoorIndexEntry = {
  assetId: number;
  planId: number;
  subLocationId: number | null;
  doorNumber: string;
  assetName: string;
  gradeFinal: number | null;
  statusLabel: string;
  planTitle: string;
  subLocationLabel: string;
  point: PortalPlanPoint;
};

type HoverInfo = {
  assetId: number;
  clientX: number;
  clientY: number;
};

const PLAN_ZOOM_MIN = 0.5;
const PLAN_ZOOM_MAX = 8;
const PLAN_ZOOM_STEP = 0.25;

function clampMarkerOverlayScale(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.min(4, Math.max(0.25, n));
}

function clampZoom(value: number): number {
  return Math.min(PLAN_ZOOM_MAX, Math.max(PLAN_ZOOM_MIN, Number(value.toFixed(2))));
}

function denormalize(
  x: unknown,
  y: unknown,
  imageWidth: number,
  imageHeight: number,
): { x: number; y: number } | null {
  const nx = clampNormalized(x);
  const ny = clampNormalized(y);
  if (nx === null || ny === null) return null;
  return {
    x: nx * Math.max(1, imageWidth),
    y: ny * Math.max(1, imageHeight),
  };
}

function formatDateTime(value?: string | null): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function doorNumberOf(point: PortalPlanPoint): string {
  return (
    String(point.label_text || point.asset_external_id || point.asset_name || "").trim() ||
    `#${point.asset_id}`
  );
}

/** Derive Unterorte from plans when API omits sub_locations. */
function resolveSubLocations(detail: PortalWartungDetail) {
  if (Array.isArray(detail.sub_locations) && detail.sub_locations.length > 0) {
    return detail.sub_locations;
  }
  const byId = new Map<
    number,
    {
      id: number;
      name?: string | null;
      path_label?: string;
      path_segments?: string[];
      plan_ids: number[];
      active_plan_id: number | null;
      has_plan: boolean;
    }
  >();
  for (const plan of detail.plaene || []) {
    const sid = plan.sub_location_id;
    if (sid == null || sid <= 0) continue;
    const existing = byId.get(sid);
    if (existing) {
      if (!existing.plan_ids.includes(plan.id)) existing.plan_ids.push(plan.id);
      continue;
    }
    const name =
      (plan as { sub_location_name?: string | null }).sub_location_name ||
      `Unterort #${sid}`;
    byId.set(sid, {
      id: sid,
      name,
      path_label: name,
      path_segments: [name],
      plan_ids: [plan.id],
      active_plan_id: plan.id,
      has_plan: true,
    });
  }
  return Array.from(byId.values());
}

export function WartungPlanViewer({ projectId, detail, onClose }: Props) {
  const subLocations = useMemo(() => resolveSubLocations(detail), [detail]);
  const plaene = detail.plaene || [];
  const ergebnisse = detail.ergebnisse || [];

  const ergebnisByAssetId = useMemo(() => {
    const map = new Map<number, PortalWartungErgebnis>();
    for (const item of ergebnisse) {
      if (item.asset_id == null) continue;
      if (!map.has(item.asset_id)) map.set(item.asset_id, item);
    }
    return map;
  }, [ergebnisse]);

  const ergebnisByEventId = useMemo(() => {
    const map = new Map<number, PortalWartungErgebnis>();
    for (const item of ergebnisse) {
      map.set(item.id, item);
    }
    return map;
  }, [ergebnisse]);

  const initialSubId = useMemo(() => {
    const withPlan = subLocations.find((s) => s.has_plan);
    return (withPlan || subLocations[0])?.id ?? null;
  }, [subLocations]);

  const [selectedSubId, setSelectedSubId] = useState<number | null>(initialSubId);
  const [imageHref, setImageHref] = useState("");
  const [imageSize, setImageSize] = useState({ width: 1, height: 1 });
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [planZoom, setPlanZoom] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlightedAssetId, setHighlightedAssetId] = useState<number | null>(null);
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const [modalAssetId, setModalAssetId] = useState<number | null>(null);

  const svgRef = useRef<SVGSVGElement>(null);
  const imageRef = useRef<SVGImageElement>(null);
  const layerRef = useRef<SVGGElement>(null);
  const hintRef = useRef<HTMLParagraphElement>(null);
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const panRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startScrollLeft: number;
    startScrollTop: number;
    moved: boolean;
  } | null>(null);
  const suppressClickUntilRef = useRef(0);
  const pendingScrollAssetIdRef = useRef<number | null>(null);
  const pendingZoomRef = useRef<number | null>(null);
  const planZoomRef = useRef(planZoom);
  planZoomRef.current = planZoom;

  const selectedSub = useMemo(
    () => subLocations.find((s) => s.id === selectedSubId) || null,
    [subLocations, selectedSubId],
  );

  const activePlan = useMemo(() => {
    if (!selectedSub) return null;
    const preferredId = selectedSub.active_plan_id;
    if (preferredId != null) {
      const match = plaene.find((p) => p.id === preferredId);
      if (match) return match;
    }
    return (
      plaene.find((p) => p.sub_location_id === selectedSub.id) ||
      plaene.find((p) => selectedSub.plan_ids.includes(p.id)) ||
      null
    );
  }, [plaene, selectedSub]);

  const doorIndex = useMemo(() => {
    const entries: DoorIndexEntry[] = [];
    const subById = new Map(subLocations.map((s) => [s.id, s]));
    for (const plan of plaene) {
      const sub = plan.sub_location_id != null ? subById.get(plan.sub_location_id) : null;
      for (const point of plan.points || []) {
        const ergebnis =
          (point.event_id != null ? ergebnisByEventId.get(point.event_id) : null) ||
          ergebnisByAssetId.get(point.asset_id) ||
          null;
        entries.push({
          assetId: point.asset_id,
          planId: plan.id,
          subLocationId: plan.sub_location_id ?? null,
          doorNumber: doorNumberOf(point),
          assetName: String(point.asset_name || "").trim(),
          gradeFinal:
            point.grade_final ??
            (ergebnis?.grade_final != null ? Number(ergebnis.grade_final) : null),
          statusLabel:
            point.result_status_label ||
            ergebnis?.result_status_label ||
            "Keine Bewertung",
          planTitle: String(plan.title || "Plan").trim(),
          subLocationLabel: String(sub?.path_label || sub?.name || "").trim(),
          point,
        });
      }
    }
    return entries;
  }, [plaene, subLocations, ergebnisByAssetId, ergebnisByEventId]);

  const searchMatches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return doorIndex
      .filter((entry) => {
        const haystack = `${entry.doorNumber} ${entry.assetName}`.toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 12);
  }, [doorIndex, searchQuery]);

  const resolveDoorDetails = useCallback(
    (assetId: number | null) => {
      if (assetId == null) return null;
      const entry = doorIndex.find((d) => d.assetId === assetId) || null;
      if (!entry) return null;
      const ergebnis =
        (entry.point.event_id != null
          ? ergebnisByEventId.get(entry.point.event_id)
          : null) ||
        ergebnisByAssetId.get(assetId) ||
        null;
      return { entry, ergebnis };
    },
    [doorIndex, ergebnisByAssetId, ergebnisByEventId],
  );

  const hoverDetails = resolveDoorDetails(hoverInfo?.assetId ?? null);
  const modalDetails = resolveDoorDetails(modalAssetId);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        if (modalAssetId != null) {
          setModalAssetId(null);
          return;
        }
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalAssetId, onClose]);

  useEffect(() => {
    const onDocDown = (ev: MouseEvent) => {
      if (!searchWrapRef.current?.contains(ev.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const prevUrl = blobUrlRef.current;
    blobUrlRef.current = null;
    if (prevUrl) URL.revokeObjectURL(prevUrl);

    if (!activePlan?.id || activePlan.has_file === false) {
      setImageHref("");
      setImageSize({ width: 1, height: 1 });
      setLoadState(activePlan ? "error" : "idle");
      setLoadError(activePlan ? "Für diesen Unterort ist keine Plan-Datei hinterlegt." : null);
      return () => {
        cancelled = true;
      };
    }

    setLoadState("loading");
    setLoadError(null);
    setPlanZoom(pendingZoomRef.current ?? 1);
    pendingZoomRef.current = null;

    void (async () => {
      try {
        const url = await fetchAuthenticatedBlobUrl(
          `/portal/wartungen/${projectId}/plaene/${activePlan.id}/image`,
        );
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        blobUrlRef.current = url;
        const metrics = await new Promise<{ width: number; height: number }>((resolve) => {
          const img = new Image();
          img.onload = () =>
            resolve({
              width: Math.max(1, img.naturalWidth || 1),
              height: Math.max(1, img.naturalHeight || 1),
            });
          img.onerror = () => resolve({ width: 1, height: 1 });
          img.src = url;
        });
        if (cancelled) return;
        setImageHref(url);
        setImageSize(metrics);
        setLoadState("ready");
      } catch (err) {
        if (cancelled) return;
        setImageHref("");
        setImageSize({ width: 1, height: 1 });
        setLoadState("error");
        setLoadError(
          err instanceof ApiError ? err.message : "Planbild konnte nicht geladen werden.",
        );
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [activePlan?.id, activePlan?.has_file, projectId]);

  const updateCanvasMetrics = useCallback(() => {
    const svg = svgRef.current;
    const wrap = canvasWrapRef.current;
    if (!svg || !wrap) return;
    const imageWidth = Math.max(1, imageSize.width);
    const imageHeight = Math.max(1, imageSize.height);
    const containerWidth = Math.max(1, wrap.clientWidth - 24);
    const containerHeight = Math.max(1, wrap.clientHeight - 24);
    const fitScale = Math.min(containerWidth / imageWidth, containerHeight / imageHeight);
    const effectiveScale = Math.max(fitScale * planZoom, 0.0001);
    const scaledWidth = imageWidth * effectiveScale;
    const scaledHeight = imageHeight * effectiveScale;
    const isZoomedBeyondFit = planZoom > 1.001;
    // Explizite Pixelgröße + flex-shrink:0, sonst staucht Flex das SVG auf Containerbreite.
    svg.style.width = `${scaledWidth}px`;
    svg.style.height = `${scaledHeight}px`;
    svg.style.flexShrink = "0";
    svg.style.maxWidth = isZoomedBeyondFit ? "none" : "100%";
    svg.style.maxHeight = isZoomedBeyondFit ? "none" : "100%";
    wrap.dataset.planZoomState = isZoomedBeyondFit ? "zoomed" : "fit";
  }, [imageSize.height, imageSize.width, planZoom]);

  const scrollMarkerIntoView = useCallback((assetId: number) => {
    const wrap = canvasWrapRef.current;
    const marker = layerRef.current?.querySelector(
      `[data-asset-id="${assetId}"]`,
    ) as SVGGElement | null;
    if (!wrap || !marker) return;
    const wrapRect = wrap.getBoundingClientRect();
    const markerRect = marker.getBoundingClientRect();
    const markerCenterX = markerRect.left + markerRect.width / 2;
    const markerCenterY = markerRect.top + markerRect.height / 2;
    const deltaX = markerCenterX - (wrapRect.left + wrapRect.width / 2);
    const deltaY = markerCenterY - (wrapRect.top + wrapRect.height / 2);
    wrap.scrollLeft += deltaX;
    wrap.scrollTop += deltaY;
  }, []);

  const renderOverlay = useCallback(() => {
    const svg = svgRef.current;
    const image = imageRef.current;
    const layer = layerRef.current;
    if (!svg || !image || !layer) return;

    if (!activePlan || loadState !== "ready" || !imageHref) {
      renderPlanSvg({
        svg,
        image,
        layer,
        hint: hintRef.current,
        plan: null,
        emptyHintText: selectedSub
          ? selectedSub.has_plan
            ? loadError || "Plan wird geladen …"
            : "Für diesen Unterort ist noch kein Plan hinterlegt."
          : "Bitte einen Unterort auswählen.",
      });
      return;
    }

    const imageWidth = Math.max(1, imageSize.width);
    const imageHeight = Math.max(1, imageSize.height);
    const scale = clampMarkerOverlayScale(activePlan.marker_overlay_scale);
    const planPage = Number(activePlan.page_number || activePlan.page || 1) || 1;

    const markers: PlanMarkerInput[] = (activePlan.points || [])
      .filter((pt) => Number(pt.page_number || planPage) === planPage)
      .map((pt) => {
        const anchor = denormalize(pt.x, pt.y, imageWidth, imageHeight);
        const labelPos = denormalize(pt.label_x, pt.label_y, imageWidth, imageHeight);
        const sym = pt.plan_label_symbol;
        const labelSvgMarkup = String(sym?.svg_markup || "").trim();
        const labelPngBase64 = String(sym?.png_preview_base64 || "").trim();
        const hasSymbol = Boolean(labelSvgMarkup || labelPngBase64);
        const fallbackLabel =
          !labelPos && anchor
            ? { x: anchor.x + 48 * scale, y: anchor.y - 40 * scale }
            : null;
        const effectiveLabel = labelPos || fallbackLabel;
        const labelText = doorNumberOf(pt);
        const isHighlighted = highlightedAssetId === pt.asset_id;
        const grade = pt.grade_final != null ? Number(pt.grade_final) : null;
        return {
          point: anchor,
          label: effectiveLabel,
          labelText,
          labelSvgMarkup,
          labelPngBase64,
          labelSymbolSize: (hasSymbol ? 52 : 30) * scale,
          labelSymbolGap: 10 * scale,
          pointRadius: (isHighlighted ? 9 : 7.5) * scale,
          haloRadius: (isHighlighted ? 18 : 14) * scale,
          accentColor: getGradeColor(grade),
          highlighted: isHighlighted,
          labelPaddingX: 8,
          labelPaddingY: 4,
          labelRadiusX: 8,
          labelRadiusY: 8,
          labelFontSize: 12,
          labelStrokeWidth: 0,
          labelBadgeStrokeWidth: 1.5,
          pointTabIndex: 0,
          pointRole: "button",
          pointAriaLabel: `Tür ${labelText}`,
          dataset: { assetId: pt.asset_id },
          pointDataset: { assetId: pt.asset_id },
          labelDataset: { assetId: pt.asset_id },
          labelTabIndex: 0,
          labelRole: "button",
          labelAriaLabel: `Tür ${labelText}`,
        };
      });

    renderPlanSvg({
      svg,
      image,
      layer,
      hint: hintRef.current,
      plan: activePlan,
      viewBox: `0 0 ${imageWidth} ${imageHeight}`,
      imageHref,
      imageWidth,
      imageHeight,
      markers,
      hintText: `${markers.length} Positionen · Mausrad zoomen · Ziehen zum Verschieben`,
    });
    updateCanvasMetrics();

    if (pendingScrollAssetIdRef.current != null) {
      const targetId = pendingScrollAssetIdRef.current;
      pendingScrollAssetIdRef.current = null;
      requestAnimationFrame(() => scrollMarkerIntoView(targetId));
    }
  }, [
    activePlan,
    highlightedAssetId,
    imageHref,
    imageSize.height,
    imageSize.width,
    loadError,
    loadState,
    scrollMarkerIntoView,
    selectedSub,
    updateCanvasMetrics,
  ]);

  useEffect(() => {
    renderOverlay();
  }, [renderOverlay]);

  useEffect(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      updateCanvasMetrics();
      renderOverlay();
    });
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [renderOverlay, updateCanvasMetrics]);

  useEffect(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap) return;

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const currentZoom = planZoomRef.current;
      const direction = event.deltaY > 0 ? -1 : 1;
      const nextZoom = clampZoom(currentZoom + direction * PLAN_ZOOM_STEP);
      if (nextZoom === currentZoom) return;

      const rect = wrap.getBoundingClientRect();
      const offsetX = event.clientX - rect.left + wrap.scrollLeft;
      const offsetY = event.clientY - rect.top + wrap.scrollTop;
      const ratio = nextZoom / currentZoom;

      setPlanZoom(nextZoom);
      requestAnimationFrame(() => {
        const svg = svgRef.current;
        if (svg) {
          const imageWidth = Math.max(1, imageSize.width);
          const imageHeight = Math.max(1, imageSize.height);
          const containerWidth = Math.max(1, wrap.clientWidth - 24);
          const containerHeight = Math.max(1, wrap.clientHeight - 24);
          const fitScale = Math.min(containerWidth / imageWidth, containerHeight / imageHeight);
          const effectiveScale = Math.max(fitScale * nextZoom, 0.0001);
          const scaledWidth = imageWidth * effectiveScale;
          const scaledHeight = imageHeight * effectiveScale;
          const isZoomedBeyondFit = nextZoom > 1.001;
          svg.style.width = `${scaledWidth}px`;
          svg.style.height = `${scaledHeight}px`;
          svg.style.flexShrink = "0";
          svg.style.maxWidth = isZoomedBeyondFit ? "none" : "100%";
          svg.style.maxHeight = isZoomedBeyondFit ? "none" : "100%";
          wrap.dataset.planZoomState = isZoomedBeyondFit ? "zoomed" : "fit";
        }
        wrap.scrollLeft = Math.max(0, offsetX * ratio - (event.clientX - rect.left));
        wrap.scrollTop = Math.max(0, offsetY * ratio - (event.clientY - rect.top));
      });
    };

    wrap.addEventListener("wheel", onWheel, { passive: false });
    return () => wrap.removeEventListener("wheel", onWheel);
  }, [imageSize.height, imageSize.width]);

  const eventTargetAssetId = (target: EventTarget | null): number | null => {
    const raw = (target as Element | null)
      ?.closest?.("[data-asset-id]")
      ?.getAttribute("data-asset-id");
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const focusDoor = useCallback(
    (entry: DoorIndexEntry) => {
      setSearchQuery(entry.doorNumber);
      setSearchOpen(false);
      setHighlightedAssetId(entry.assetId);
      pendingScrollAssetIdRef.current = entry.assetId;

      if (entry.subLocationId != null && entry.subLocationId !== selectedSubId) {
        pendingZoomRef.current = 2.25;
        setSelectedSubId(entry.subLocationId);
        return;
      }

      if (planZoom < 1.75) setPlanZoom(2.25);
      requestAnimationFrame(() => {
        updateCanvasMetrics();
        scrollMarkerIntoView(entry.assetId);
      });
    },
    [planZoom, scrollMarkerIntoView, selectedSubId, updateCanvasMetrics],
  );

  const endPan = useCallback((pointerId?: number) => {
    const pan = panRef.current;
    if (!pan) return;
    if (pointerId != null && pan.pointerId !== pointerId) return;
    const svg = svgRef.current;
    try {
      if (svg?.hasPointerCapture(pan.pointerId)) {
        svg.releasePointerCapture(pan.pointerId);
      }
    } catch {
      /* ignore */
    }
    if (pan.moved) suppressClickUntilRef.current = Date.now() + 250;
    canvasWrapRef.current?.classList.remove("is-panning");
    panRef.current = null;
  }, []);

  const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    if (event.button !== 0) return;
    const wrap = canvasWrapRef.current;
    if (!wrap) return;
    const canPanH = wrap.scrollWidth - wrap.clientWidth > 1;
    const canPanV = wrap.scrollHeight - wrap.clientHeight > 1;
    if (!canPanH && !canPanV && planZoom <= 1.001) return;

    // Vorherigen Drag sauber beenden (z. B. nach lostpointercapture).
    endPan();

    panRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startScrollLeft: wrap.scrollLeft,
      startScrollTop: wrap.scrollTop,
      moved: false,
    };
    wrap.classList.add("is-panning");
    // Capture muss auf demselben Element liegen wie die React-Handler (SVG),
    // sonst gehen pointerup/move an den Wrapper und der Drag bleibt hängen.
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
    event.preventDefault();
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!panRef.current || panRef.current.pointerId !== event.pointerId || !canvasWrapRef.current) {
      return;
    }
    const dx = event.clientX - panRef.current.startClientX;
    const dy = event.clientY - panRef.current.startClientY;
    if (!panRef.current.moved && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) {
      panRef.current.moved = true;
      setHoverInfo(null);
    }
    canvasWrapRef.current.scrollLeft = panRef.current.startScrollLeft - dx;
    canvasWrapRef.current.scrollTop = panRef.current.startScrollTop - dy;
    event.preventDefault();
  };

  const endPointer = (event: React.PointerEvent<SVGSVGElement>) => {
    endPan(event.pointerId);
  };

  const handleSvgClick = (event: React.MouseEvent<SVGSVGElement>) => {
    if (Date.now() < suppressClickUntilRef.current) return;
    const assetId = eventTargetAssetId(event.target);
    if (!assetId) return;
    setHighlightedAssetId(assetId);
    setModalAssetId(assetId);
    setHoverInfo(null);
  };

  const handleSvgMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    if (panRef.current?.moved) return;
    const assetId = eventTargetAssetId(event.target);
    if (!assetId) {
      if (hoverInfo) setHoverInfo(null);
      return;
    }
    setHoverInfo({
      assetId,
      clientX: event.clientX,
      clientY: event.clientY,
    });
  };

  const handleSvgMouseLeave = () => {
    setHoverInfo(null);
  };

  const ortLabel =
    detail.project.ort_name ||
    detail.project.auftragsnummer ||
    `Projekt #${projectId}`;

  return (
    <div className="wartung-plan-overlay" role="dialog" aria-modal="true" aria-label="Planansicht">
      <aside className="wartung-plan-sidebar">
        <div className="wartung-plan-sidebar__header">
          <h2 className="wartung-plan-sidebar__title">{ortLabel}</h2>
          <p className="wartung-plan-sidebar__meta">
            {[detail.project.auftragsnummer, detail.project.kunde_name]
              .filter(Boolean)
              .join(" · ") || "Unterorte"}
          </p>
        </div>
        <ul className="wartung-plan-sidebar__list">
          {subLocations.length === 0 ? (
            <li className="px-4 py-3 text-sm text-[#5b6672]">Keine Unterorte vorhanden.</li>
          ) : (
            subLocations.map((sub) => {
              const active = sub.id === selectedSubId;
              return (
                <li key={sub.id}>
                  <button
                    type="button"
                    className={[
                      "wartung-plan-sidebar__item",
                      active ? "is-active" : "",
                      !sub.has_plan ? "is-empty" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      setSelectedSubId(sub.id);
                      setHighlightedAssetId(null);
                    }}
                  >
                    {sub.path_label || sub.name || `Unterort #${sub.id}`}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </aside>

      <section className="wartung-plan-main">
        <div className="wartung-plan-toolbar">
          <p className="wartung-plan-toolbar__label">
            {activePlan?.title ||
              selectedSub?.path_label ||
              selectedSub?.name ||
              "Plan"}
          </p>
          <div className="wartung-plan-search" ref={searchWrapRef}>
            <input
              type="search"
              className="wartung-plan-search__input"
              placeholder="Türnummer suchen…"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              aria-label="Türnummer suchen"
              autoComplete="off"
            />
            {searchOpen && searchQuery.trim() && (
              <div className="wartung-plan-search__dropdown" role="listbox">
                {searchMatches.length === 0 ? (
                  <p className="wartung-plan-search__empty">Keine Tür gefunden.</p>
                ) : (
                  searchMatches.map((entry) => (
                    <button
                      key={`${entry.planId}-${entry.assetId}`}
                      type="button"
                      className="wartung-plan-search__option"
                      role="option"
                      onClick={() => focusDoor(entry)}
                    >
                      <span className="wartung-plan-search__option-main">
                        <span
                          className="wartung-plan-search__grade"
                          style={{ background: getGradeColor(entry.gradeFinal) }}
                          aria-hidden
                        />
                        <strong>{entry.doorNumber}</strong>
                        {entry.assetName && entry.assetName !== entry.doorNumber ? (
                          <span>{entry.assetName}</span>
                        ) : null}
                      </span>
                      <span className="wartung-plan-search__option-meta">
                        {[entry.subLocationLabel, entry.planTitle].filter(Boolean).join(" · ")}
                        {entry.gradeFinal != null ? ` · Note ${entry.gradeFinal}` : ""}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="wartung-plan-toolbar__actions">
            <div className="wartung-plan-zoom" aria-label="Plan-Zoom">
              <button
                type="button"
                className="wartung-plan-btn"
                disabled={planZoom <= PLAN_ZOOM_MIN}
                onClick={() => setPlanZoom((z) => clampZoom(z - PLAN_ZOOM_STEP))}
                aria-label="Verkleinern"
              >
                −
              </button>
              <button
                type="button"
                className="wartung-plan-btn wartung-plan-zoom__value"
                onClick={() => setPlanZoom(1)}
                aria-label="Zoom zurücksetzen"
                title="Zoom zurücksetzen"
              >
                {Math.round(planZoom * 100)}%
              </button>
              <button
                type="button"
                className="wartung-plan-btn"
                disabled={planZoom >= PLAN_ZOOM_MAX}
                onClick={() => setPlanZoom((z) => clampZoom(z + PLAN_ZOOM_STEP))}
                aria-label="Vergrößern"
              >
                +
              </button>
            </div>
            <button type="button" className="wartung-plan-btn" onClick={onClose}>
              Schließen
            </button>
          </div>
        </div>

        <div className="wartung-plan-viewport">
          <div className="wartung-plan-legend" aria-label="Notenlegende">
            {[1, 2, 3, 4, 5, 6].map((grade) => (
              <span key={grade} className="wartung-plan-legend__item">
                <span
                  className="wartung-plan-legend__swatch"
                  style={{ background: GRADE_COLORS[grade] }}
                />
                Note {grade}
              </span>
            ))}
          </div>
          <div
            ref={canvasWrapRef}
            className="wartung-plan-canvas-wrap"
            data-plan-zoom-state="fit"
          >
          <svg
            ref={svgRef}
            className="wartung-plan-svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1 1"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
            onLostPointerCapture={endPointer}
            onClick={handleSvgClick}
            onMouseMove={handleSvgMouseMove}
            onMouseLeave={handleSvgMouseLeave}
          >
            <image
              ref={imageRef}
              className="wartung-plan-image"
              href=""
              x="0"
              y="0"
              width="1"
              height="1"
            />
            <g ref={layerRef} />
          </svg>
          {loadState === "loading" && (
            <div className="wartung-plan-loading">Plan wird geladen …</div>
          )}
          {loadState === "error" && loadError && (
            <div className="wartung-plan-error">{loadError}</div>
          )}
          {!activePlan && selectedSub && (
            <div className="wartung-plan-empty">
              Für diesen Unterort ist noch kein Plan hinterlegt.
            </div>
          )}
          <p ref={hintRef} className="wartung-plan-hint" />
          </div>
        </div>
      </section>

      {hoverInfo && hoverDetails && modalAssetId == null && (
        <div
          className="wartung-plan-tooltip"
          style={{
            left: Math.min(hoverInfo.clientX + 16, window.innerWidth - 280),
            top: Math.min(hoverInfo.clientY + 16, window.innerHeight - 180),
          }}
          role="tooltip"
        >
          <p className="wartung-plan-tooltip__title">{hoverDetails.entry.doorNumber}</p>
          {hoverDetails.entry.assetName &&
          hoverDetails.entry.assetName !== hoverDetails.entry.doorNumber ? (
            <p className="wartung-plan-tooltip__sub">{hoverDetails.entry.assetName}</p>
          ) : null}
          <dl className="wartung-plan-tooltip__grid">
            <div>
              <dt>Wartungsnote</dt>
              <dd>
                <span
                  className="wartung-plan-grade-pill"
                  style={{
                    background: getGradeColor(hoverDetails.entry.gradeFinal),
                  }}
                >
                  {hoverDetails.entry.gradeFinal != null
                    ? `Note ${hoverDetails.entry.gradeFinal}`
                    : "Keine Note"}
                </span>
              </dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{hoverDetails.entry.statusLabel}</dd>
            </div>
            <div>
              <dt>Mängel</dt>
              <dd>
                {hoverDetails.ergebnis?.defect_count != null
                  ? hoverDetails.ergebnis.defect_count
                  : "–"}
              </dd>
            </div>
            <div>
              <dt>Typ</dt>
              <dd>{hoverDetails.ergebnis?.type_name || "–"}</dd>
            </div>
          </dl>
          <p className="wartung-plan-tooltip__hint">Klicken für alle Details</p>
        </div>
      )}

      {modalDetails && (
        <div
          className="wartung-plan-modal-backdrop"
          role="presentation"
          onClick={() => setModalAssetId(null)}
        >
          <div
            className="wartung-plan-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Details zu Tür ${modalDetails.entry.doorNumber}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="wartung-plan-modal__header">
              <div>
                <p className="wartung-plan-modal__eyebrow">Türdetails</p>
                <h3 className="wartung-plan-modal__title">{modalDetails.entry.doorNumber}</h3>
                {modalDetails.entry.assetName &&
                modalDetails.entry.assetName !== modalDetails.entry.doorNumber ? (
                  <p className="wartung-plan-modal__sub">{modalDetails.entry.assetName}</p>
                ) : null}
              </div>
              <button
                type="button"
                className="wartung-plan-btn"
                onClick={() => setModalAssetId(null)}
              >
                Schließen
              </button>
            </div>

            <div className="wartung-plan-modal__grade-row">
              <span
                className="wartung-plan-grade-pill wartung-plan-grade-pill--lg"
                style={{
                  background: getGradeColor(modalDetails.entry.gradeFinal),
                }}
              >
                {modalDetails.entry.gradeFinal != null
                  ? `Wartungsnote ${modalDetails.entry.gradeFinal}`
                  : "Keine Wartungsnote"}
              </span>
              <span className="wartung-plan-modal__status">
                {modalDetails.entry.statusLabel}
              </span>
            </div>

            <dl className="wartung-plan-modal__grid">
              <div>
                <dt>Türnummer</dt>
                <dd>{modalDetails.entry.doorNumber}</dd>
              </div>
              <div>
                <dt>Bezeichnung</dt>
                <dd>{modalDetails.entry.assetName || "–"}</dd>
              </div>
              <div>
                <dt>Wartungstyp</dt>
                <dd>{modalDetails.ergebnis?.type_name || "–"}</dd>
              </div>
              <div>
                <dt>Ergebnisstatus</dt>
                <dd>{modalDetails.entry.statusLabel}</dd>
              </div>
              <div>
                <dt>Anzahl Mängel</dt>
                <dd>
                  {modalDetails.ergebnis?.defect_count != null
                    ? modalDetails.ergebnis.defect_count
                    : "–"}
                </dd>
              </div>
              <div>
                <dt>Durchgeführt am</dt>
                <dd>{formatDateTime(modalDetails.ergebnis?.performed_at)}</dd>
              </div>
              <div>
                <dt>Abgeschlossen am</dt>
                <dd>{formatDateTime(modalDetails.ergebnis?.completed_at)}</dd>
              </div>
              <div>
                <dt>Unterort</dt>
                <dd>{modalDetails.entry.subLocationLabel || "–"}</dd>
              </div>
              <div>
                <dt>Plan</dt>
                <dd>{modalDetails.entry.planTitle || "–"}</dd>
              </div>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
